import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAssistant } from '../../Context/AssistantContext';

const STATUS_LABEL = {
    executed: 'Done',
    failed: 'Failed',
    unknown: 'Needs checking',
    cancelled: 'Cancelled',
    edited: 'Edited',
    expired: 'Expired',
    confirmed: 'Working on it',
};

/* values longer than this get a textarea */
const LONG_VALUE = 40;

/* One proposed action. Nothing happens until Confirm is pressed; what runs is
   what the server stored for this card, not anything shown here. */
const ActionCard = ({ action }) => {
    const { confirm, cancel, revise, sending } = useAssistant();
    /* the field values being edited, or null when the card is not in edit mode */
    const [draft, setDraft] = useState(null);
    const fields = action.fields || [];
    const pending = action.status === 'pending';
    const edited = action.edited && action.status === 'cancelled';
    const state = edited ? 'edited' : action.status;
    const busy = action.busy || action.revising;

    const startEdit = () => setDraft(fields.map((field) => String(field.value ?? '')));

    const setValue = (index, value) => setDraft((current) => current.map((v, i) => (i === index ? value : v)));

    const save = (event) => {
        event.preventDefault();
        const changes = fields
            .map((field, i) => ({ label: field.label, value: draft[i].trim() }))
            .filter((field, i) => field.value !== String(fields[i].value ?? '').trim());
        setDraft(null);
        if (changes.length) revise(action, changes);
    };

    return (
        <div className={`assistant-card is-${state}${busy ? ' is-busy' : ''}`} data-testid="assistant-card">
            <b className="assistant-card-title">{action.title}</b>

            {draft ? (
                <form className="assistant-card-form" onSubmit={save}>
                    {fields.map((field, i) => (
                        <label key={field.label}>
                            <span>{field.label}</span>
                            {String(field.value ?? '').length > LONG_VALUE ? (
                                <textarea rows={3} value={draft[i]} onChange={(e) => setValue(i, e.target.value)} />
                            ) : (
                                <input type="text" value={draft[i]} onChange={(e) => setValue(i, e.target.value)} />
                            )}
                        </label>
                    ))}
                    <div className="assistant-card-actions">
                        <button type="submit" className="is-primary" disabled={sending}>Save changes</button>
                        <button type="button" onClick={() => setDraft(null)}>Back</button>
                    </div>
                </form>
            ) : (
                <dl className="assistant-card-fields">
                    {fields.map((field) => (
                        <div key={`${field.label}:${field.value}`}>
                            <dt>{field.label}</dt>
                            <dd>{field.value}</dd>
                        </div>
                    ))}
                </dl>
            )}

            {pending && !draft && (
                <div className="assistant-card-actions">
                    <button type="button" className="is-primary" disabled={busy} onClick={() => confirm(action)}>
                        Confirm
                    </button>
                    <button type="button" disabled={busy || sending} onClick={startEdit}>Edit</button>
                    <button type="button" disabled={busy} onClick={() => cancel(action)}>Cancel</button>
                </div>
            )}

            {!pending && (
                <p className="assistant-card-status">
                    <span className="assistant-card-pill">{STATUS_LABEL[state] || state}</span>
                    {edited && <span className="assistant-card-hint">Updated card below.</span>}
                    {action.result && action.result.message && <span> {action.result.message}</span>}
                    {action.result && action.result.reference && (
                        <span className="assistant-card-ref"> Reference: {action.result.reference}</span>
                    )}
                </p>
            )}

            {action.error && <p className="assistant-card-error" role="alert">{action.error}</p>}
            {action.notice && <p className="assistant-card-error" role="alert">{action.notice}</p>}

            {action.link && (
                <Link className="assistant-card-link" to={action.link.path}>{action.link.label}</Link>
            )}
        </div>
    );
};

export default ActionCard;
